import { type ChangeEvent, useEffect, useRef, useState } from "react";
import posthog, { isPostHogConfigured } from "../../src/posthog";
import { AnalysisResultPanel } from "../components/analysis-result-panel";
import { CapturePanel } from "../components/capture-panel";
import type { FoodAnalysisResponse } from "../domains/food-analysis/food-analysis.model";
import { requestAccountDeletion } from "../services/account.service";
import { CameraService } from "../services/camera.service";
import {
	AuthenticationRequiredError,
	FoodAnalysisService,
} from "../services/food-analysis.service";
import { isSupportedImage, resizeImageFile } from "../services/image.service";
import { getMyProfile } from "../services/profile.service";

const foodAnalysisService = new FoodAnalysisService(
	import.meta.env.VITE_AI_API_URL || "",
);

interface FoodScannerPageProps {
	accessToken: string;
	user: { name?: string; email?: string };
	onAuthenticationRequired: () => void;
	onLogout: () => void;
}

function errorMessage(error: unknown, fallback: string): string {
	return error instanceof Error ? error.message : fallback;
}

export default function FoodScannerPage({
	accessToken,
	user,
	onAuthenticationRequired,
	onLogout,
}: FoodScannerPageProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const cameraServiceRef = useRef<CameraService | null>(null);
	if (!cameraServiceRef.current) {
		cameraServiceRef.current = new CameraService();
	}
	const cameraService = cameraServiceRef.current;

	const [cameraOn, setCameraOn] = useState(false);
	const [image, setImage] = useState<string | null>(null);
	const [result, setResult] = useState<FoodAnalysisResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [profileName, setProfileName] = useState<string | null>(null);

	useEffect(() => () => cameraService.stop(), [cameraService]);

	// Fetch enriched profile name from public.profiles (RLS: auth.uid() = id)
	// Manual RLS verify: sign in as user A, try select where id = user B -> empty due to policy
	useEffect(() => {
		if (!import.meta.env.VITE_SUPABASE_URL) return;
		getMyProfile()
			.then((p) => {
				if (p?.first_name || p?.last_name) {
					setProfileName([p.first_name, p.last_name].filter(Boolean).join(" "));
				}
			})
			.catch(() => {});
	}, []);

	async function startCamera() {
		const video = videoRef.current;
		if (!video) return;

		setError("");
		try {
			await cameraService.start(video);
			if (isPostHogConfigured) posthog.capture("camera_started");
			setCameraOn(true);
			setImage(null);
			setResult(null);
		} catch {
			setError("Không thể mở camera. Hãy cho phép truy cập hoặc tải ảnh lên.");
		}
	}

	function capture() {
		const video = videoRef.current;
		if (!video) return;

		try {
			const capturedImage = cameraService.capture(video);
			if (!capturedImage) {
				setError(
					`Không thể xử lý ảnh [CAMERA_NOT_READY]. Camera chưa có khung hình hợp lệ. Kích thước: ${video.videoWidth}×${video.videoHeight}; readyState: ${video.readyState}. Hãy chờ một giây rồi chụp lại.`,
				);
				return;
			}
			setImage(capturedImage);
			if (isPostHogConfigured) posthog.capture("image_captured");
			setError("");
		} catch (error) {
			const detail =
				error instanceof Error
					? `${error.name}: ${error.message}`
					: String(error);
			setError(
				`Không thể xử lý ảnh [CAMERA_CAPTURE]. Bước: sao chép khung hình camera và tạo JPEG. Kích thước: ${video.videoWidth}×${video.videoHeight}; readyState: ${video.readyState}. Chi tiết: ${detail}`,
			);
		}

		cameraService.stop();
		setCameraOn(false);
		setResult(null);
	}

	async function upload(event: ChangeEvent<HTMLInputElement>) {
		const input = event.currentTarget;
		const file = input.files?.[0];
		if (!file) return;

		if (!isSupportedImage(file)) {
			setError("Vui lòng chọn một tệp hình ảnh.");
			input.value = "";
			return;
		}

		setError("");
		try {
			setImage(await resizeImageFile(file));
			if (isPostHogConfigured) {
				posthog.capture("image_uploaded", {
					image_type: file.type || "unknown",
				});
			}
			setResult(null);
		} catch (error) {
			setError(errorMessage(error, "Không thể xử lý ảnh."));
		} finally {
			input.value = "";
		}
	}

	async function analyze() {
		if (!image) return;

		setLoading(true);
		setError("");
		setResult(null);
		try {
			const analysis = await foodAnalysisService.analyze(image, accessToken);
			setResult(analysis);
			if (isPostHogConfigured) {
				posthog.capture("food_analysis_completed", {
					result_count: analysis.results.length,
					truncated: analysis.truncated,
				});
			}
		} catch (error) {
			if (error instanceof AuthenticationRequiredError) {
				if (isPostHogConfigured) {
					posthog.capture("food_analysis_failed", {
						failure_reason: "authentication_required",
					});
				}
				onAuthenticationRequired();
				return;
			}
			if (isPostHogConfigured) {
				posthog.capture("food_analysis_failed", {
					failure_reason: "analysis_error",
				});
			}
			setError(errorMessage(error, "Đã có lỗi xảy ra."));
		} finally {
			setLoading(false);
		}
	}

	function reset() {
		cameraService.stop();
		setCameraOn(false);
		setImage(null);
		setResult(null);
		setError("");
	}

	return (
		<main>
			<header className="topbar">
				<a
					className="brand"
					href="#top"
					aria-label="Gout Food Scanner — trang chủ"
				>
					<span className="brand-mark">G</span>
					<span>Gout Food Scanner</span>
				</a>
				<div className="account-actions">
					<span className="privacy-pill">
						<span className="dot" /> Ảnh không được lưu
					</span>
					<span className="account-name">
						{profileName || user.name || user.email || "Tài khoản"}
					</span>
					<button type="button" className="logout-button" onClick={onLogout}>
						Đăng xuất
					</button>
				</div>
			</header>

			<section className="hero" id="top">
				<div className="eyebrow">Trợ lý nhận diện thực phẩm</div>
				<h1>
					Chụp món ăn.
					<br />
					<em>Hiểu rõ hơn.</em>
				</h1>
				<p className="intro">
					Nhận diện thực phẩm bằng AI và tham khảo mức độ phù hợp cho người bị
					gout và thừa cân / béo phì cho từng đối tượng trong ảnh.
				</p>
			</section>

			<section className="workspace" aria-label="Phân tích thực phẩm">
				<CapturePanel
					cameraOn={cameraOn}
					error={error}
					image={image}
					loading={loading}
					videoRef={videoRef}
					onAnalyze={analyze}
					onCapture={capture}
					onReset={reset}
					onStartCamera={startCamera}
					onUpload={upload}
				/>
				<AnalysisResultPanel
					loading={loading}
					result={result}
					onReset={reset}
				/>
			</section>

			<section className="how-it-works">
				<p className="section-kicker">Cách hoạt động</p>
				<div className="steps-grid">
					<article>
						<b>01</b>
						<h3>Chụp rõ món ăn</h3>
						<p>
							Dùng camera sau hoặc tải ảnh có sẵn. Đặt các đối tượng rõ nét,
							không che nhau.
						</p>
					</article>
					<article>
						<b>02</b>
						<h3>AI nhận diện</h3>
						<p>
							Mô hình thị giác xác định từng đối tượng và mức độ chắc chắn
							riêng.
						</p>
					</article>
					<article>
						<b>03</b>
						<h3>Đánh giá gout và cân nặng</h3>
						<p>
							AI cân nhắc purine, khẩu phần và cách chế biến để đưa ra gợi ý dễ
							hiểu.
						</p>
					</article>
				</div>
			</section>

			<footer>
				<p>
					<strong>Lưu ý y tế:</strong> Kết quả do AI tạo, chỉ mang tính tham
					khảo và có thể sai. Không dùng để chẩn đoán hoặc thay thế tư vấn của
					bác sĩ/chuyên gia dinh dưỡng, đặc biệt khi đang có cơn gout cấp hoặc
					dùng thuốc.
				</p>
				{import.meta.env.VITE_SUPABASE_URL && (
					<p>
						<button
							type="button"
							className="logout-button"
							onClick={async () => {
								if (
									!confirm(
										"Bạn chắc chắn muốn xóa tài khoản? Hành động này không thể hoàn tác và sẽ xóa hồ sơ của bạn.",
									)
								)
									return;
								try {
									await requestAccountDeletion(accessToken);
									if (isPostHogConfigured) posthog.capture("account_deleted");
									alert("Tài khoản đã được xóa.");
									onLogout();
								} catch (e) {
									alert(
										e instanceof Error ? e.message : "Không thể xóa tài khoản.",
									);
								}
							}}
						>
							Xóa tài khoản
						</button>
					</p>
				)}
			</footer>
		</main>
	);
}
